import * as BrandDefinition from 'modules/BrandDefinition';
import { IList } from 'typings/DeepIMap';
import * as Constants from 'const';
import * as Models from 'models';
import { SegmentType, ParagraphType, CellEntityType } from 'services/ArtboardConverter/constants';
import { Schemas } from 'services/ArtboardConverter/models';
import { getAlignment, addInvisibleJoinersToUrls } from 'services/ArtboardConverter/utils';
import { getReferenceIndent } from 'services/ArtboardConverter/utils/getReferenceIndent';
import { getIntegerFromStyle } from 'utils/getIntegerFromStyle';
import { getReferencesAsString } from 'utils/getReferencesAsString';
import { createLexicalReferencesEditor } from '../editors';
import { ArtboardConverterStyles, convertLexicalEditorState } from './convertLexicalEditorState';
import { paragraphDefaultValues } from './factories';

function transformParagraphsToList(
  paragraphs: Schemas.CombinedParagraph[],
  projectType: Constants.ProjectType,
): Schemas.ListParagraph {
  const { margin, padding } = getReferenceIndent(projectType);

  // bulletWidth and listStyleType have no values - it reproduces initial behaviour
  const extraOptions = {
    level: 0,
  } as Pick<Schemas.ListItemParagraph, 'level' | 'bulletWidth' | 'listStyleType'>;

  return {
    margin,
    padding,
    type: ParagraphType.UNORDERED_LIST,
    paragraphs: paragraphs.map((item): Schemas.ListItemParagraph => {
      if ((item as Schemas.ListParagraph).paragraphs) {
        const { type } = item as Schemas.ListParagraph;

        return {
          type,
          ...paragraphDefaultValues,
          ...extraOptions,
          segments: [],
        };
      }

      return ({
        ...(item as Schemas.TextParagraph),
        type: ParagraphType.UNORDERED_LIST_ITEM,
        ...extraOptions,
      });
    }),
  };
}

type Context = {
  projectType: Constants.ProjectType;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  brandStyle: Models.BrandStyleMap | undefined;
  referencesOrder: IList<Models.ReferenceCitation>;
  surfaces: Models.ScreensOrderedMap;
};

export function createReferencesListSchema(
  relation: Models.RelationMap<Models.ReferenceCitationElementStyles>,
  context: Context,
): Schemas.ReferenceText | undefined {
  const {
    projectType,
    colors, fonts, brandStyle,
    referencesOrder, surfaces,
  } = context;

  if (referencesOrder.size === 0) {
    return;
  }

  const fontColor = relation.getIn(['styles', 'fontColor']);
  const fontSize = relation.getIn(['styles', 'fontSize']);
  const lineHeight = relation.getIn(['styles', 'lineHeight']);
  const alignment = relation.getIn(['styles', 'alignment']);
  const brandFontName = relation.getIn(['styles', 'fontFamily']);
  const brandFontStyle = relation.getIn(['styles', 'fontStyle']);
  const fontRef = brandFontName ? { fontName: brandFontName, characterStyleName: brandFontStyle } : undefined;
  const fontFamily = BrandDefinition.getBrandFontValueFontFamily(fonts, fontRef);

  const html = getReferencesAsString(referencesOrder, projectType);
  const editor = createLexicalReferencesEditor(html);

  const converterOptions = {
    brandStyle, colors, fonts,
    projectType,
    surfaces,
    paragraphLineHeightNoPx: true,
  };

  const defaultStyles: ArtboardConverterStyles = {
    color: fontColor ?? Constants.DefaultCustomStyle.FONT_COLOR,
    partColor: fontColor ?? Constants.DefaultCustomStyle.FONT_COLOR,
    bulletColor: '',
    fontSize: getIntegerFromStyle(fontSize) || Constants.DefaultCustomStyle.FONT_SIZE,
    fontFamily,
    partFontFamily: fontFamily,
    lineHeight: parseFloat(lineHeight ?? '0') || Constants.TextLineHeightValue.ONE_POINT_FIVE,
    paragraphAlign: getAlignment(alignment).align,
  };

  const paragraphs = convertLexicalEditorState(
    editor.getEditorState(),
    converterOptions,
    defaultStyles,
  );
  const schema: Schemas.ReferenceText = { paragraphs, entityType: CellEntityType.TEXT, isReferenceCitation: true };

  if (projectType === Constants.ProjectType.EMAIL) {
    schema.paragraphs.forEach((paragraph) => {
      const { segments = [] } = paragraph as Schemas.TextParagraph;
      segments.forEach((segment) => {
        segment.parts.forEach((part) => {
          if (segment.type === SegmentType.TEXT) {
            part.text = addInvisibleJoinersToUrls(part.text);
          }
        });
      });
    });
  }

  const { wrapReferenceInOrderedList } = Constants.ProjectsConfig[projectType];
  if (wrapReferenceInOrderedList) {
    schema.paragraphs = [transformParagraphsToList(schema.paragraphs, projectType)];
  }

  return schema;
}
